<template>
	<div
		class="drawer-item-category"
		:class="{
			[categoryId]: true,
			visible: chats.length > 0,
			'menu-open': $refs.menu?.$refs?.menu?.isOpen,
		}"
	>
		<div
			class="drawer-item-contents drawer-item visible"
			v-if="chats.length > 0"
			@click="isExpanded = !isExpanded"
			@keyup.enter.prevent.stop="isExpanded = !isExpanded"
		>
			<div class="drawer-item-title">
				<component v-if="categoryIcon" :is="categoryIcon" type="regular" />
				<div class="drawer-item-title-text" v-text="category.displayname" />
			</div>
			<div class="drawer-item-actions">
				<UnreadBadge
					v-if="showBadge"
					class="drawer-item-badge"
					:class="{ 'non-mentions': !totalMentions }"
					:unread="totalMentions || totalUnread"
				/>

				<div
					v-if="isLive"
					class="drawer-item-live"
					v-t="'meetings_live_livebutton'"
				/>

				<DotsMenu
					v-if="menuOptions.length > 0"
					class="drawer-item-menu"
					localize
					:items="menuOptions"
					@select="onMenuItemClick"
					@keyup.enter.stop.prevent="$refs.menu?.$refs?.menu?.toggleMenu()"
					ref="menu"
				/>

				<CaretIcon class="drawer-caret" :up="isExpanded" :down="!isExpanded" />
			</div>
		</div>

		<transition-group
			name="drawer-items"
			class="drawer-item-children"
			:class="{ expanded: isExpanded }"
			tag="div"
			:style="cssStyles"
		>
			<SidebarChat
				v-for="chat in chats"
				:key="chat.id"
				:chat="chat"
				:isPinned="isPinned"
				:isRecent="isRecents"
				:isVisible="isExpanded"
			/>
		</transition-group>

		<NicknameModal
			v-if="showNicknameModal"
			:categoryId="categoryId"
			@close="showNicknameModal = false"
		/>
	</div>
</template>

<script>
import { DotsMenu, UnreadBadge } from '@/components/common'
import {
	CaretIcon,
	DotMenuIcon,
	ClockIcon,
	ThumbtackIcon,
	CommentIcon,
} from '@/icons'
import {
	getStat,
	getChats,
	updateStat,
	getCategory,
	getChatsForCategory,
	getPinnedChats,
	getRecentChatsToDisplay,
	getDMChats,
	getVideosession,
	getChatsWithNoCategory,
	sortChatsByDisplayName,
	getMyUser,
	sortChatsByLastMessage,
} from '@/store'
import { post } from '@/helpers/api'
import SidebarChat from './SidebarChat'
import { closeNotificationsForBubble } from '@/helpers/pushNotification'
import NicknameModal from './NicknameModal'
import debounce from 'debounce'
import { updateClientMeta } from '@/api/user'
import { markChatAsRead } from '@/api/membership'
import { categorySortTypes } from '@/helpers/constants'

const DRAWER_ITEM_HEIGHT = 42
export const PINNED = 'pinned'
export const RECENTS = 'recents'
export const DM = 'dms'
export const OTHER = 'other'

const getExpandedCategories = () => {
	const defaultIds = [PINNED, RECENTS, DM, OTHER]
	try {
		return JSON.parse(localStorage.getItem('expandedCategories')) || defaultIds
	} catch (e) {}
	return defaultIds
}

export default {
	name: 'SidebarCategory',
	props: {
		categoryId: [Number, String],
	},
	components: {
		DotsMenu,
		UnreadBadge,
		CaretIcon,
		DotMenuIcon,
		SidebarChat,
		NicknameModal,
	},

	data() {
		return {
			category: getCategory(this.categoryId) || {},
			chats: [],
			currentSort: null,
			isExpanded: getExpandedCategories().includes(this.categoryId),
			showNicknameModal: false,
		}
	},

	created() {
		this.$onEventBus({
			'chat-update': debounce(this.refreshData, 25),
			'expand-category': (categoryId) => {
				if (this.categoryId === categoryId) {
					this.isExpanded = true
				}
			},
			'my-user-update': () => {
				if (!this.isDMs) return
				const sort = this.getCurrentDMSort()
				this.refreshDMData(sort)
			},
		})
		this.refreshData()
	},

	computed: {
		isPinned() {
			return this.categoryId === PINNED
		},
		isRecents() {
			return this.categoryId === RECENTS
		},
		isOther() {
			return this.categoryId === OTHER
		},
		isDMs() {
			return this.categoryId === DM
		},
		showMenu() {
			return !this.isOther && !this.isDMs
		},
		totalUnread() {
			if (!this.canShowBadge) return 0
			return this.chats.reduce((sum, c) => sum + (c?.unread ?? 0), 0)
		},
		totalMentions() {
			if (!this.canShowBadge) return 0
			return this.chats.reduce((sum, c) => {
				if (c?.unread_mentions) return sum + c.unread_mentions
				if (c?.marked_unread) return sum + 1
				return sum
			}, 0)
		},
		hasMarkedUnread() {
			if (!this.canShowBadge) return 0
			return this.chats.some((c) => c.marked_unread)
		},
		hasUnread() {
			return this.totalUnread > 0
		},
		isSystemCategory() {
			return this.isPinned || this.isRecents || this.isOther || this.isDMs
		},
		canShowBadge() {
			return this.isPinned || this.isRecents
		},
		showBadge() {
			return (
				this.canShowBadge &&
				!this.isExpanded &&
				(this.hasUnread || this.hasMarkedUnread)
			)
		},
		isLive() {
			return (
				this.canShowBadge &&
				!this.isExpanded &&
				this.chats.some(
					(c) =>
						!c.mute && getVideosession(c.videosession_id)?.status == 'ACTIVE'
				)
			)
		},

		menuOptions() {
			if (this.hasUnread || this.hasMarkedUnread) {
				return [
					{
						id: 'markasread',
						label: 'chat_drawer_action_markallread',
					},
				]
			}
			if (!this.isSystemCategory) {
				return [{ id: 'nickname', label: 'chat_drawer_action_nickname' }]
			}
			if (this.isDMs) {
				const isByName = this.currentSort === categorySortTypes.NAME
				return [
					{
						id: 'sort_dms',
						label: this.$t(
							isByName ? 'common_sort_by_recent' : 'common_sort_by_name'
						),
					},
				]
			}
			return []
		},

		cssStyles() {
			if (this.isExpanded) {
				const totalHeight = this.chats.length * DRAWER_ITEM_HEIGHT
				return { 'max-height': `${totalHeight}px` }
			}
			return { 'max-height': 0 }
		},

		categoryIcon() {
			if (this.isPinned) return ThumbtackIcon
			if (this.isRecents) return ClockIcon
			if (this.isDMs) return CommentIcon
			return null
		},
	},

	watch: {
		isExpanded(isExpanded) {
			const idSet = new Set(getExpandedCategories())
			isExpanded ? idSet.add(this.categoryId) : idSet.delete(this.categoryId)
			localStorage.setItem(
				'expandedCategories',
				JSON.stringify(Array.from(idSet))
			)
		},
	},

	methods: {
		refreshData() {
			if (this.isPinned) {
				return this.refreshPinnedData()
			}
			if (this.isRecents) {
				return this.refreshRecentsData()
			}
			if (this.isOther) {
				return this.refreshOtherData()
			}
			if (this.isDMs) {
				const sortType = this.getCurrentDMSort()
				return this.refreshDMData(sortType)
			}
			return this.refreshCategoryData()
		},

		refreshPinnedData() {
			this.chats = getPinnedChats()
			this.category = {
				id: this.categoryId,
				displayname: this.$t('chat_drawer_section_pinned'),
			}
		},

		refreshRecentsData() {
			this.chats = getRecentChatsToDisplay()
			this.category = {
				id: this.categoryId,
				displayname: this.$t('chat_drawer_section_recents'),
			}
		},

		refreshOtherData() {
			this.chats = getChatsWithNoCategory().sort(sortChatsByDisplayName)
			this.category = {
				id: this.categoryId,
				displayname: this.$t('chat_drawer_section_bubbles_uncatagorized'),
			}
		},

		refreshDMData(sortType) {
			this.chats = getDMChats().sort(
				sortType === categorySortTypes.RECENT
					? sortChatsByLastMessage
					: sortChatsByDisplayName
			)
			this.currentSort = sortType
			this.category = {
				id: this.categoryId,
				displayname: this.$t('chat_drawer_section_directmessages'),
			}
		},

		refreshCategoryData() {
			this.category = getCategory(this.categoryId) || {}
			this.chats = (getChatsForCategory(this.categoryId) || []).sort(
				sortChatsByDisplayName
			)
		},

		onMenuItemClick({ id }) {
			switch (id) {
				case 'markasread':
					return this.markAllAsRead()
				case 'nickname':
					this.showNicknameModal = true
					return
				case 'sort_dms':
					return this.toggleDMSort()
			}
		},

		markAllAsRead() {
			getChats().forEach((c) => this.markAsRead(c))
		},

		async markAsRead(chat) {
			if (chat.marked_unread) {
				this.unmarkAsUnread(chat)
			}
			if (chat.unread) {
				this.updateChatMark(chat)
			}
		},

		async unmarkAsUnread(chat) {
			try {
				await markChatAsRead(chat.id)
			} catch (e) {
				this.$alert.error(e)
			}
		},

		async updateChatMark(chat) {
			const { latest_message_id, mark, unread, unread_mentions } = getStat(
				chat.id
			)
			updateStat(chat.id, { unread: 0, unread_mentions: 0 })
			try {
				const { data } = await post('/api/v1/bubble.mark', {
					bubble_id: chat.id,
					message_id: latest_message_id,
				})
				if (data.ok) {
					updateStat(chat.id, {
						unread: 0,
						unread_mentions: 0,
						mark: latest_message_id,
					})

					closeNotificationsForBubble(chat.id)
				} else {
					updateStat(chat.id, { unread, unread_mentions, mark })
				}
				// if not ok, reset unread
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
				updateStat(chat.id, { unread, unread_mentions })
			}
		},

		getCurrentDMSort() {
			const { client_meta } = getMyUser() || {}
			this.currentSort =
				client_meta?.sidebar_dms_sort_by || categorySortTypes.NAME
			return this.currentSort
		},

		async toggleDMSort() {
			try {
				const currSort = this.getCurrentDMSort()
				const newSort =
					currSort === categorySortTypes.NAME
						? categorySortTypes.RECENT
						: categorySortTypes.NAME
				this.refreshDMData(newSort)
				await updateClientMeta({ sidebar_dms_sort_by: newSort })
			} catch (e) {
				console.error(e)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$gap: 6px;

.drawer-item-category {
	&.visible {
		margin-bottom: 12px;
	}
	&.menu-open {
		.drawer-item-menu {
			width: 20px !important;
			opacity: 1 !important;
			:deep(.actions) {
				background: var(--neutral-300) !important;
			}
		}
	}
	.drawer-item-contents {
		cursor: pointer;
		@include flex-spread(4px);
		padding: 0 8px 0 12px;
		flex-grow: 1;
		overflow: hidden;
		height: 30px;
		// TODO: work with design to switch this to a standardized color at some point
		background: #ececec;
		margin-bottom: 4px;

		.drawer-item-title {
			@include flex(8px);
			@include fontFamily(900);
			@include ellipsis;
			font-size: 12px;
			color: var(--neutral-600);
			text-transform: uppercase;
			letter-spacing: 1.4px;
			width: 100%;
			max-width: 100%;
			.pronto-icon {
				color: var(--neutral-600);
				font-size: 14px;
			}
			.drawer-item-title-text {
				width: 100%;
				@include ellipsis;
			}
		}
		&:hover {
			.drawer-item-menu {
				width: 20px !important;
				opacity: 1 !important;
			}
		}
		&:focus-within {
			.drawer-item-menu {
				width: 20px !important;
				opacity: 1 !important;
			}
		}
	}
	.drawer-item-children {
		opacity: 0;
		// padding-left: 6px;
		max-height: 0;
		overflow: hidden;
		transition: all ease-in-out 0.3s;
		transition-property: max-height, opacity;
		&.expanded {
			opacity: 1;
		}
	}

	.drawer-items-move {
		transition: transform 0.3s;
	}

	.drawer-item-actions {
		@include flex($gap);
		.pronto-icon {
			color: var(--neutral-500);
		}
		:deep(.pronto-icon) {
			color: var(--neutral-500);
		}
		.drawer-item-badge {
			&.non-mentions {
				color: var(--neutral-300);
				background: var(--neutral-0);
			}
		}
		.drawer-caret {
			margin: 0 -2px;
			color: var(--neutral-400);
		}
		.drawer-item-menu {
			width: 0;
			opacity: 0;
			overflow: hidden;
			transition: all ease-in-out 100ms;
			transition-property: width, opacity;
			:deep(.actions) {
				width: 20px;
				height: 25px;
				overflow: hidden;
				padding: 0;
				border-radius: 3px;
				font-size: 18px;
				&:hover {
					background: var(--neutral-0) !important;
				}
			}
		}
		.drawer-item-live {
			background-color: var(--red-400);
			@include fontFamily(900);
			font-size: 12px;
			color: var(--neutral-0);
			padding: 2px 4px;
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.drawer-item-category {
		.drawer-item-actions {
			.drawer-item-badge {
				&.non-mentions {
					color: var(--neutral-800) !important;
					background: var(--neutral-100);
				}
			}
			.drawer-caret {
				color: var(--neutral-500);
			}
		}
		.drawer-item-contents {
			background: var(--neutral-100);
		}
	}
}
</style>
